import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { combineLatest, Observable, of, Subscription } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Member } from 'src/app/features/member/member'
import { memberActions } from 'src/app/features/member/member.actions'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { EmailAddressValidator } from 'src/app/shared/validators/email-address.validator'
import { CompetitionStaffFunction } from '../../competition-staff-function/competition-staff-function'
import { competitionStaffFunctionActions } from '../../competition-staff-function/competition-staff-function.actions'
import { competitionStaffFunctionSelectors } from '../../competition-staff-function/competition-staff-function.selectors'
import { competitionSelectors } from '../../competition.selectors'
import { scrollToFirstInvalidControl } from 'src/app/shared/utils'
import { NotificationService } from 'src/app/core/notification.service'
import { CompetititionFuctionService } from 'src/app/shared/services/competition.function.service'
import { ActivatedRoute } from '@angular/router'
@UntilDestroy()
@Component({
  selector: 'competition-add-member-card',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionAddMemberCardComponent implements OnInit, OnDestroy {
  @Input() role: string
  @Input() isSubmitButtonDisabled: boolean = false
  @Output() onSubmit = new EventEmitter<any>()
  public teamId: number
  public team$: Observable<CompetitionTeam>
  public selectedMember: Member
  public members$: Observable<Member[]>
  public selectedMember$: Observable<Member>
  public form: UntypedFormGroup
  public competitionStaffFunctions$: Observable<CompetitionStaffFunction[]>
  private competitionSportId: number
  private subscriptions: Subscription[] = []
  public functions

  constructor(
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    public competititionFuctionService: CompetititionFuctionService,
    private notificationService: NotificationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      memberId: [null],
      email: [null, [Validators.required, EmailAddressValidator()]],
      functionId: [null, []]
    })

    const urlParts = window.location.pathname.split('/')
    this.teamId = Number(urlParts[urlParts.length - 1])

    this.team$ = this.store.select(
      competitionTeamSelectors.selectById,
      this.teamId
    )

    if (this.role == 'player') {
      this.form.addControl('number', new UntypedFormControl(null, Validators.required))
      this.getFunctionPlayers()
    }

    combineLatest([
      this.team$,
      this.store.select(competitionSelectors.selectEntities)
    ])
      .pipe(take(1))
      .subscribe(([team, competitionEntities]) => {
        this.competitionSportId =
          competitionEntities[team?.competitionId]?.competitionSportId

        this.store.dispatch(
          competitionStaffFunctionActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                competitionSportId: this.competitionSportId
              }
            })
          })
        )
      })

    this.competitionStaffFunctions$ = this.store.select(
      competitionStaffFunctionSelectors.selectByFilter,
      {
        field: 'competitionSportId',
        value: this.competitionSportId
      }
    )

    this.competitionStaffFunctions$
      .pipe(untilDestroyed(this))
      .subscribe((staffFunctions) => {
        if (this.role == 'staff' && staffFunctions?.length) {
          this.form.controls['functionId'].setValidators([Validators.required])
        }
      })
  }

  private getFunctionPlayers() {
    this.team$.pipe(take(1)).subscribe((teamId) => {
      let competition
      this.store
        .select(competitionSelectors.selectEntities)
        .pipe(take(1))
        .subscribe((comp) => {
          competition = comp[teamId?.competitionId]
        })
      const params = RequestQueryBuilder.create({
        search: {
          competitionSportId: competition?.competitionSportId
        }
      })
      this.competititionFuctionService
        .getFunctionPlayers(params)
        .pipe(take(1))
        .subscribe((items) => {
          if (items.length > 0) {
            this.form.controls['functionId'].setValidators([
              Validators.required
            ])
          }
          this.functions = items
        })
    })
  }
  public onSearchMember(term: string) {
    if (this.form.controls.email.status == 'VALID') {
      this.store.dispatch(
        memberActions.searchManyByParams({
          // TODO should shift to another endpoint as /members is now for admins only
          params: RequestQueryBuilder.create({
            search: {
              $or: [
                {
                  email: {
                    [CondOperator.EQUALS_LOW]: term
                  }
                }
              ]
            }
          })
        })
      )

      this.selectedMember$ = this.store
        .select(memberSelectors.selectAll)
        .pipe(
          map(
            (members) =>
              members.filter(
                (member) => member?.email?.toLowerCase() == term.toLowerCase()
              )[0]
          )
        )

      this.subscriptions.push(
        this.selectedMember$.subscribe((member) => {
          if (member) {
            this.form.controls.memberId.setValue(member.id)
            this.selectedMember = member
          } else {
            this.selectedMember = null
          }
        })
      )
    }
  }

  public onSelectMember(member: Member) {
    this.selectedMember = member
  }

  public onFormSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })

      scrollToFirstInvalidControl(this.elementRef, this.notificationService)
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    this.team$.pipe(take(1)).subscribe((team) => {
      switch (this.role) {
        case 'staff':
          this.onSubmit.emit({
            memberId: this.selectedMember ? this.selectedMember.id : null,
            email: this.form.value.email,
            competitionId: team?.competitionId,
            competitionTeamId: team?.id,
            functionId: this.form.value.functionId
              ? Number(this.form.value.functionId)
              : null,
            role: 'staff'
          })

          break
        case 'player':
          if (this.form.invalid) {
            Object.keys(this.form.controls).forEach((key) => {
              this.form.controls[key].markAsDirty()
            })

            console.log('%cForm is invalid', 'color: red; font-weight: bold;')
            return
          }
          this.onSubmit.emit({
            memberId: this.selectedMember ? this.selectedMember.id : null,
            email: this.form.value.email,
            competitionId: team?.competitionId,
            competitionTeamId: team?.id,
            functionId: this.form.value.functionId
              ? Number(this.form.value.functionId)
              : null,
            number: this.form.value.number,
            role: 'player'
          })

          break
      }
    })
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
